import { Input } from "@/components/ui/input";
import { type LucideIcon } from "lucide-react";

import React from "react";
import {
  type ControllerRenderProps,
  type FieldValues,
  type UseFormReturn,
} from "react-hook-form";

const FormattedNumberField = <FV extends FieldValues>({
  field,
  form,
  className,
  leftIcon,
}: {
  field: ControllerRenderProps<FV>;
  form: UseFormReturn<FV>;
  className?: string;
  leftIcon?: LucideIcon;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [formattedValue, setFormattedValue] = React.useState<string>("");

  React.useEffect(() => {
    setFormattedValue(() =>
      form.formState.defaultValues?.[field.name]?.toLocaleString("en-US", {
        minimumFractionDigits: 0,
      }),
    );
  }, [form.formState.defaultValues]);

  const handleInputChange = (value: string) => {
    const currentValue = parseFloat(value.replace?.(/,/g, "")) || 0;
    field.onChange(currentValue);
    setFormattedValue(() =>
      currentValue.toLocaleString("en-US", {
        minimumFractionDigits: 0,
      }),
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      incrementValue();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      decrementValue();
    }
  };

  const incrementValue = () => {
    const incrementedValue = field.value + 1;
    handleInputChange(incrementedValue.toString());
  };

  const decrementValue = () => {
    const decrementedValue = field.value - 1;
    handleInputChange(decrementedValue.toString());
  };

  return (
    <Input
      ref={inputRef}
      leftIcon={leftIcon}
      type="text"
      className={className}
      value={formattedValue}
      onChange={(e) => handleInputChange(e.target.value)}
      onKeyDown={(e) => handleKeyDown(e)}
    />
  );
};

export default FormattedNumberField;
